import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/client';
import { ErrorBox, Spinner } from '../components/ui';

/**
 * New Project form — the heart of data entry. A project is a site under a
 * programme; the user names it and adds its buildings, each flagged HRB or
 * LRB. On save the backend generates the full plan (stages + substages),
 * so the info panel previews what that expansion will produce before the
 * user commits. This is exactly the flow from the agreed prototype.
 */
export default function NewProject() {
  const { programmeId } = useParams();
  const nav = useNavigate();
  const [programme, setProgramme] = useState(null);
  const [buildingTypes, setBuildingTypes] = useState([]);
  const [form, setForm] = useState({ reference: '', name: '', startDate: '', expectedEndDate: '', budgetAmount: '' });
  const [buildings, setBuildings] = useState([{ name: '', buildingType: 'HRB', storeys: '', units: '' }]);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/programmes/${programmeId}`).then(r => setProgramme(r.data)).catch(setErr);
    api.get('/building-types').then(r => setBuildingTypes(r.data)).catch(() => setBuildingTypes([{ code: 'HRB' }, { code: 'LRB' }]));
    api.get('/suggest-reference', { params: { kind: 'project' } }).then(r => setForm(f => ({ ...f, reference: r.data.reference }))).catch(() => {});
  }, [programmeId]);

  const setB = (i, k, v) => setBuildings(bs => bs.map((b, j) => j === i ? { ...b, [k]: v } : b));
  const addB = () => setBuildings(bs => [...bs, { name: '', buildingType: 'LRB', storeys: '', units: '' }]);
  const rmB = i => setBuildings(bs => bs.length > 1 ? bs.filter((_, j) => j !== i) : bs);

  const hasHrb = buildings.some(b => b.buildingType === 'HRB');
  const perBuildingCount = buildings.length; // construction works + handover per building

  const submit = async e => {
    e.preventDefault(); setErr(null);
    const named = buildings.filter(b => b.name.trim());
    if (!named.length) return setErr(new Error('Add at least one named building'));
    if (form.budgetAmount !== '' && !(Number(form.budgetAmount) >= 0))
      return setErr(new Error('The budgeted amount must be a positive number'));
    setBusy(true);
    try {
      const r = await api.post('/projects', {
        programmeId: Number(programmeId), reference: form.reference, name: form.name,
        startDate: form.startDate || null, expectedEndDate: form.expectedEndDate || null,
        budgetAmount: form.budgetAmount === '' ? null : Number(form.budgetAmount),
        buildings: named.map(b => ({
          name: b.name.trim(), buildingType: b.buildingType,
          storeys: b.storeys ? Number(b.storeys) : null, units: b.units ? Number(b.units) : null
        }))
      });
      nav(`/projects/${r.data.id}`);
    } catch (e2) { setErr(e2); setBusy(false); }
  };

  if (!programme) return <Spinner label="Loading programme…" />;

  return (
    <>
      <div className="phead">
        <div><h1 style={{ marginBottom: 2 }}>New project</h1>
          <div className="muted">under {programme.programme_name} · contractor {programme.contractor_name}</div></div>
      </div>
      <div className="panel">
        <ErrorBox error={err} />
        <form onSubmit={submit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12 }}>
            <div className="field"><label>Reference</label>
              <input style={{ width: '100%' }} value={form.reference} onChange={e => setForm({ ...form, reference: e.target.value })} required /></div>
            <div className="field"><label>Project / site name</label>
              <input style={{ width: '100%' }} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required /></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div className="field"><label>Start date</label>
              <input type="date" style={{ width: '100%' }} value={form.startDate} onChange={e => setForm({ ...form, startDate: e.target.value })} /></div>
            <div className="field"><label>Expected end date</label>
              <input type="date" style={{ width: '100%' }} value={form.expectedEndDate} onChange={e => setForm({ ...form, expectedEndDate: e.target.value })} /></div>
            <div className="field"><label>Overall budgeted amount</label>
              <input type="number" min="0" step="0.01" style={{ width: '100%' }} placeholder="0.00"
                value={form.budgetAmount} onChange={e => setForm({ ...form, budgetAmount: e.target.value })} />
              <div className="muted" style={{ marginTop: 3 }}>
                Spend is recorded against this on the project's expenditure page.
              </div></div>
          </div>

          <div style={{ borderTop: '1px solid var(--line)', paddingTop: 12, marginTop: 4 }}>
            <div className="phead"><h3 style={{ margin: 0 }}>Buildings</h3>
              <button type="button" onClick={addB}>+ Add building</button></div>
            <table className="data">
              <thead><tr><th style={{ width: '42%' }}>Name</th><th style={{ width: '18%' }}>Type</th><th style={{ width: '17%' }}>Storeys</th><th style={{ width: '17%' }}>Units</th><th></th></tr></thead>
              <tbody>
                {buildings.map((b, i) => (
                  <tr key={i}>
                    <td><input style={{ width: '100%' }} value={b.name} onChange={e => setB(i, 'name', e.target.value)} placeholder="Block name" /></td>
                    <td><select value={b.buildingType} onChange={e => setB(i, 'buildingType', e.target.value)}>
                      {buildingTypes.map(t => <option key={t.code} value={t.code}>{t.code}</option>)}</select></td>
                    <td><input type="number" style={{ width: '100%' }} value={b.storeys} onChange={e => setB(i, 'storeys', e.target.value)} /></td>
                    <td><input type="number" style={{ width: '100%' }} value={b.units} onChange={e => setB(i, 'units', e.target.value)} /></td>
                    <td><button type="button" onClick={() => rmB(i)} disabled={buildings.length === 1}>Remove</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="alert info" style={{ marginTop: 14 }}>
            On save, a full plan is generated automatically:
            {' '}<strong>{hasHrb ? 11 : 10} stages</strong>
            {hasHrb ? ' (Gateway 3 included because there is an HRB building)' : ' (no Gateway 3 — no HRB building)'}.
            {' '}Construction works and handover split into <strong>{perBuildingCount} row{perBuildingCount > 1 ? 's' : ''} each</strong>, one per building.
            Every task starts as <strong>Not started</strong>; from then on its status is worked
            out from its dates — <strong>Outstanding</strong> once its start date arrives with no
            actual start recorded, <strong>In progress</strong> the day after it starts, and
            <strong> Delayed</strong> if its expected end date passes before it finishes.
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button className="primary" disabled={busy}>{busy ? 'Creating plan…' : 'Create project and generate plan'}</button>
            <button type="button" onClick={() => nav(`/programmes/${programmeId}`)}>Cancel</button>
          </div>
        </form>
      </div>
    </>
  );
}
